import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import date from 'date-and-time';

import useUIContext from '~/ui/use-ui-context';
import Loading from '~/components/misc/loading.js';
import { showSupportDialogBox } from '~/components/support/ducks/index.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { showNotification, hideNotification } from '~/components/notifications/ducks/notifications.js';

/**
 * @typedef {Object} SchufaCreditLimitProps
 * @property {string=} schufaError
 * @property {Function=} setSchufaError
 * @property {Function=} onclose
 */

const textKeys = ['SCHUFA_CONSENT_BANNER_TEXT', 'NETWORK_ERROR'];
const getNextMonth = () => {
	try {
		const now = new Date();
		now.setDate(1);
		now.setHours(0, 0, 0);
		return date.addMonths(now, 1);
	} catch {
		return new Date();
	}
};

const SchufaCreditLimitBanner = (
	/** @type SchufaCreditLimitProps */ { schufaError, setSchufaError = () => {}, onclose = false }
) => {
	const dispatch = useDispatch();
	const { withinSidebar } = useUIContext();
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);
	const message = `${t.SCHUFA_CONSENT_BANNER_TEXT} ${date.format(getNextMonth(), 'YYYY-MM-DD HH:mm')}`;

	const showSchufaNotification = useCallback(
		() =>
			dispatch(
				showNotification({
					openInSidebar: withinSidebar,
					message,
					color: 'neutral',
					buttonTextKey: 'MOBILE_RESULT_CONTACT_SUPPORT_BUTTON',
					buttonBottom: true,
					buttonCB: () => {
						if (onclose) onclose();
						dispatch(showSupportDialogBox(true));
					},
					onCloseClickCallback: () => (onclose ? onclose() : setSchufaError(null))
				})
			),
		[withinSidebar, t] // eslint-disable-line
	);

	const showNetworkError = () =>
		dispatch(
			showNotification({
				openInSidebar: withinSidebar,
				message: t.NETWORK_ERROR,
				color: 'red',
				autoHide: true,
				onCloseClickCallback: () => (onclose ? onclose() : setSchufaError(null))
			})
		);

	const removeSchufaNotification = () => {
		dispatch(hideNotification());
	};

	useEffect(() => {
		if (!isLoadingTranslations) {
			return schufaError === 'schufaNetworkError' ? showNetworkError() : showSchufaNotification();
		}
	}, [isLoadingTranslations]); // eslint-disable-line

	useEffect(() => removeSchufaNotification, []); // eslint-disable-line

	return isLoadingTranslations ? <Loading /> : null;
};

export default SchufaCreditLimitBanner;
